import { useState } from "react";
import { useParams } from "react-router-dom";
import useAuditLog from "datasets/hooks/useAuditLog";
import { auditSentence } from "datasets/utils/auditSentence";
import { AlignLeft, ChevronLeft, ChevronRight, Info, Table as TableIcon } from "lucide-react";
import { Button } from "~/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { ToggleGroup, ToggleGroupItem } from "~/components/ui/toggle-group";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { NEXTGEN_MAX_CONTENT_WIDTH } from "~/lib/layout";
import { cn } from "~/lib/utils";

// Shared "Changelog" view for a dataset's audit log, used by both the classic
// (Settings sub-tab) and nextgen (sidebar section) UIs. Built with shadcn/ui.

const PAGE_SIZE = 25;

type ChangelogView = "summary" | "table";

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

const AuditLogContent = () => {
  const [page, setPage] = useState(1);
  const [view, setView] = useState<ChangelogView>("summary");

  const { data, isLoading, error } = useAuditLog({
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
    sort: "-timestamp",
  });

  const entries = data?.items ?? [];
  const total = data?.metadata.total_count ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div
      className="mx-auto flex w-full flex-col gap-4"
      style={{ maxWidth: NEXTGEN_MAX_CONTENT_WIDTH }}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h2 className="text-2xl font-semibold">Changelog</h2>
          <p className="text-sm text-muted-foreground">
            A record of changes to this dataset — who did what, and when.
          </p>
        </div>
        {entries.length > 0 && (
          // Each item is wrapped in a Tooltip, and TooltipTrigger's asChild merge
          // overrides the toggle's data-state — so the built-in data-[state=on]
          // selected styling never fires. Drive the active-view highlight from
          // `view` instead; don't "simplify" this back to relying on data-state.
          <ToggleGroup
            type="single"
            value={view}
            onValueChange={(next) => {
              if (next) setView(next as ChangelogView);
            }}
            variant="outline"
            size="sm"
          >
            <Tooltip>
              <TooltipTrigger asChild>
                <ToggleGroupItem
                  value="summary"
                  aria-label="Summary view"
                  className={cn(view === "summary" && "bg-accent text-accent-foreground")}
                >
                  <AlignLeft className="size-4" />
                </ToggleGroupItem>
              </TooltipTrigger>
              <TooltipContent>Summary view</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <ToggleGroupItem
                  value="table"
                  aria-label="Table view"
                  className={cn(view === "table" && "bg-accent text-accent-foreground")}
                >
                  <TableIcon className="size-4" />
                </ToggleGroupItem>
              </TooltipTrigger>
              <TooltipContent>Table view</TooltipContent>
            </Tooltip>
          </ToggleGroup>
        )}
      </div>

      {isLoading ? (
        <div role="status" aria-label="Loading changelog" className="flex flex-col gap-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-12 animate-pulse rounded-lg bg-muted" />
          ))}
        </div>
      ) : error ? (
        <div className="rounded-lg border border-dashed border-border px-4 py-10 text-center">
          <p className="text-sm text-muted-foreground">
            Unable to load the changelog for this dataset.
          </p>
        </div>
      ) : entries.length > 0 ? (
        view === "summary" ? (
          <div className="rounded-lg border border-border">
            <Table>
              <TableBody>
                {entries.map((entry) => (
                  <TableRow key={entry.id} className="border-b-0">
                    <TableCell className="w-44 align-top text-muted-foreground">
                      {formatDate(entry.timestamp)}
                    </TableCell>
                    <TableCell className="whitespace-normal">{auditSentence(entry)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="rounded-lg border border-border">
            <Table className="table-fixed">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-44">Timestamp</TableHead>
                  <TableHead className="w-56">Actor</TableHead>
                  <TableHead className="w-28">Action</TableHead>
                  <TableHead>Object</TableHead>
                  <TableHead className="w-32">Type</TableHead>
                  <TableHead className="w-16">Info</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell className="text-muted-foreground">
                      {formatDate(entry.timestamp)}
                    </TableCell>
                    <TableCell className="truncate">{entry.actor}</TableCell>
                    <TableCell>{entry.action}</TableCell>
                    <TableCell className="truncate">{entry.object}</TableCell>
                    <TableCell className="text-muted-foreground">{entry.object_type}</TableCell>
                    <TableCell>
                      {entry.metadata && Object.keys(entry.metadata).length > 0 ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              aria-label="Show metadata"
                              className="text-muted-foreground hover:text-foreground"
                            >
                              <Info className="size-4" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-md">
                            <pre className="whitespace-pre font-mono text-xs">
                              {JSON.stringify(entry.metadata, null, 2)}
                            </pre>
                          </TooltipContent>
                        </Tooltip>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )
      ) : (
        <div className="rounded-lg border border-dashed border-border px-4 py-10 text-center">
          <p className="text-sm text-muted-foreground">
            No changelog entries yet. Changes to this dataset will appear here as they happen.
          </p>
        </div>
      )}

      {total > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
          </p>
          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => p - 1)}
                disabled={page === 1}
              >
                <ChevronLeft className="size-4" />
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= totalPages}
              >
                Next
                <ChevronRight className="size-4" />
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const AuditLog = () => {
  // Both routes render this component statically, and React Router keeps the
  // instance mounted across dataset switches (only the :datasetId param changes).
  // Key the content by datasetId so page/view reset on a switch and useAuditLog's
  // keepPreviousData can't bleed one dataset's entries into another.
  const { datasetId } = useParams();
  return <AuditLogContent key={datasetId} />;
};
